import { Slot } from '@radix-ui/react-slot';
import { cva } from 'class-variance-authority';
import { createContext, use, useMemo, type ComponentProps } from 'react';

import { cn } from '@/lib/utils';

import { RESULT_STATE_PLACEMENT, RESULT_STATE_SIZE, RESULT_STATE_TONE } from './types/result-state-variants-enums';

interface ResultStateContextValue {
  size: RESULT_STATE_SIZE;
  placement: RESULT_STATE_PLACEMENT;
}

const ResultStateContext = createContext<ResultStateContextValue>({
  size: RESULT_STATE_SIZE.DEFAULT,
  placement: RESULT_STATE_PLACEMENT.SECTION,
});

const foundationPlacementClasses = {
  [RESULT_STATE_PLACEMENT.INLINE]: 'flex flex-row items-start gap-3 text-left',
  [RESULT_STATE_PLACEMENT.SECTION]: 'flex flex-col items-center gap-4 py-8 text-center',
  [RESULT_STATE_PLACEMENT.PAGE]:
    'mx-auto flex max-w-[480px] flex-col items-center gap-4 py-16 text-center',
};

const foundationResultStateIconVariants = cva(
  'flex shrink-0 items-center justify-center rounded-full',
  {
    variants: {
      tone: {
        [RESULT_STATE_TONE.NEUTRAL]:
          'bg-status-neutral-surface text-status-neutral-foreground',
        [RESULT_STATE_TONE.INFO]: 'bg-status-info-surface text-status-info-foreground',
        [RESULT_STATE_TONE.SUCCESS]:
          'bg-status-success-surface text-status-success-foreground',
        [RESULT_STATE_TONE.WARNING]:
          'bg-status-warning-surface text-status-warning-foreground',
        [RESULT_STATE_TONE.ERROR]: 'bg-status-danger-surface text-status-danger-foreground',
      },
      size: {
        [RESULT_STATE_SIZE.COMPACT]: 'size-8 [&_svg]:size-4',
        [RESULT_STATE_SIZE.DEFAULT]: 'size-12 [&_svg]:size-6',
      },
    },
    defaultVariants: {
      tone: RESULT_STATE_TONE.NEUTRAL,
      size: RESULT_STATE_SIZE.DEFAULT,
    },
  }
);

interface ResultStateRootProps extends ComponentProps<'section'> {
  size?: RESULT_STATE_SIZE;
  placement?: RESULT_STATE_PLACEMENT;
}

export function ResultStateRoot({
  className,
  size = RESULT_STATE_SIZE.DEFAULT,
  placement = RESULT_STATE_PLACEMENT.SECTION,
  ...props
}: ResultStateRootProps) {
  const contextValue = useMemo(
    () => ({
      size,
      placement,
    }),
    [size, placement]
  );

  return (
    <ResultStateContext value={contextValue}>
      <section
        data-slot="result-state"
        className={cn(foundationPlacementClasses[placement], className)}
        {...props}
      />
    </ResultStateContext>
  );
}

interface ResultStateIconProps extends ComponentProps<'div'> {
  tone?: RESULT_STATE_TONE;
}

export function ResultStateIcon({
  className,
  tone,
  'aria-hidden': ariaHidden = true,
  ...props
}: ResultStateIconProps) {
  const { size } = use(ResultStateContext);

  return (
    <div
      aria-hidden={ariaHidden}
      data-slot="result-state-icon"
      className={cn(foundationResultStateIconVariants({ tone, size }), className)}
      {...props}
    />
  );
}

export function ResultStateContent({ className, ...props }: ComponentProps<'div'>) {
  const { placement } = use(ResultStateContext);

  return (
    <div
      data-slot="result-state-content"
      className={cn(
        placement === RESULT_STATE_PLACEMENT.INLINE
          ? 'flex min-w-0 flex-1 flex-col items-start gap-2'
          : 'flex flex-col items-center gap-2',
        className
      )}
      {...props}
    />
  );
}

interface ResultStateTitleProps extends ComponentProps<'h2'> {
  asChild?: boolean;
}

export function ResultStateTitle({
  asChild = false,
  className,
  ...props
}: ResultStateTitleProps) {
  const Comp = asChild ? Slot : 'h2';
  const { size } = use(ResultStateContext);

  return (
    <Comp
      data-slot="result-state-title"
      className={cn(
        'text-balance font-semibold text-content-primary',
        size === RESULT_STATE_SIZE.COMPACT ? 'type-body' : 'type-settings-h1',
        className
      )}
      {...props}
    />
  );
}

export function ResultStateDescription({ className, ...props }: ComponentProps<'p'>) {
  return (
    <p
      data-slot="result-state-description"
      className={cn(
        'max-w-sm text-pretty type-body font-normal text-content-secondary',
        className
      )}
      {...props}
    />
  );
}

export function ResultStateActions({ className, ...props }: ComponentProps<'div'>) {
  const { placement } = use(ResultStateContext);

  return (
    <div
      data-slot="result-state-actions"
      className={cn(
        placement === RESULT_STATE_PLACEMENT.INLINE
          ? 'flex flex-wrap items-center justify-start gap-2 pt-2'
          : 'flex flex-wrap items-center justify-center gap-2 pt-2',
        className
      )}
      {...props}
    />
  );
}
